/**
 * useMediaQuery
 *
 * Returns true when the given media query matches, and stays reactive as
 * the viewport crosses the query's boundary (resize, orientation change,
 * devtools device toolbar, etc.) — unlike a one-off `matchMedia(...).matches`
 * read, which several places in the codebase already do but only at mount.
 *
 * Usage:
 *   import { useMediaQuery } from '@hooks/useMediaQuery';
 *   const isWide = useMediaQuery('(min-width: 1400px)');
 */

import { useState, useEffect } from 'react';

export function useMediaQuery(query) {
  const getMatch = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler); // Safari <14 fallback
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [query]);

  return matches;
}

export default useMediaQuery;
